import clsx from "clsx";
import type { FunctionComponent } from "react";
import { useContext, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";

import Card from "~/components/Card/Card";
import { RadioGroupPlain } from "~/components/formElements/RadioGroup/RadioGroup.tsx";
import { REVENUE_REPORT_PAGE_STATE_KEY } from "~/constants/pageStateStorageKeys.ts";
import { useAggregatedDataV2 } from "~/hooks/useAggregatedDataV2.ts";
import usePageStateStorage from "~/hooks/usePageStateStorage";
import { useAllClients } from "~/modules/client/api/client/clientQueries.ts";
import { useAllCompanies } from "~/modules/client/api/company/companyQueries.ts";
import { useAllProjects } from "~/modules/project/api/project/projectQueries.ts";
import { useYearlyRevenueReport } from "~/modules/reports/api/revenueReport/revenueReportQueries.ts";
import type { RevenueBySource } from "~/modules/reports/api/revenueReport/revenueReportTypes.ts";
import DownloadYearlyRevenueReportExcelButton
	from "~/modules/reports/components/GlobalReportsView/components/RevenueReportSection/DownloadYearlyRevenueReportExcelButton";
import {
	REVENUE_SOURCE_COLORS,
} from "~/modules/reports/components/GlobalReportsView/components/RevenueReportSection/revenueSourceColors.ts";
import YearlyRevenueByProjectChart
	from "~/modules/reports/components/GlobalReportsView/components/RevenueReportSection/YearlyRevenueByProjectChart";
import { ReportsHeaderSlotContext } from "~/modules/reports/components/GlobalReportsView/reportsHeaderSlotContext.ts";
import type { YearlyRevenueChartData } from "~/modules/reports/types/revenueReportViewTypes.ts";
import LoadingPage from "~/pages/LoadingPage.tsx";
import { formatCentsToCurrency } from "~/utils/currencyUtils.ts";
import { formatDateWithGermanMonth } from "~/utils/dateAndTimeUtils.ts";

type Props = {
	year: string
};

type RevenueGrouping = "company" | "client" | "project";

type RevenueReportPageState = {
	selectedGrouping: RevenueGrouping;
	showTimeTracking: boolean;
	showInvoiced: boolean;
};

const defaultPageState: RevenueReportPageState = {
	selectedGrouping: "company",
	showTimeTracking: true,
	showInvoiced: true,
};

const groupingOptions: { value: RevenueGrouping, label: string }[] = [
	{ value: "company", label: "Kunde" },
	{ value: "client", label: "Firma" },
	{ value: "project", label: "Projekt" },
];

// Merge a source's breakdown pair into one row per entity, filling a missing source with 0.
function mergeBreakdown<T extends { revenueInYear: number }>(
	timeTracking: T[],
	invoiced: T[],
	getId: (breakdown: T) => string,
	getLabel: (id: string) => string,
): YearlyRevenueChartData[] {
	const ids = new Set<string>();
	timeTracking.forEach((breakdown) => ids.add(getId(breakdown)));
	invoiced.forEach((breakdown) => ids.add(getId(breakdown)));

	return Array.from(ids).map((id) => ({
		label: getLabel(id),
		timeTracking: timeTracking.find((breakdown) => getId(breakdown) === id)?.revenueInYear ?? 0,
		invoiced: invoiced.find((breakdown) => getId(breakdown) === id)?.revenueInYear ?? 0,
	}));
}

const RevenueReportSection: FunctionComponent<Props> = ({ year }) => {
	const { pageState, setPageState } = usePageStateStorage<RevenueReportPageState>({
		pageKey: REVENUE_REPORT_PAGE_STATE_KEY,
		defaultState: defaultPageState,
	});

	const [selectedGrouping, setSelectedGrouping] = useState<RevenueGrouping>(pageState.selectedGrouping);
	const [showTimeTracking, setShowTimeTracking] = useState(pageState.showTimeTracking);
	const [showInvoiced, setShowInvoiced] = useState(pageState.showInvoiced);

	// Persist the revenue report controls, so a reload restores them.
	useEffect(() => {
		setPageState({ selectedGrouping, showTimeTracking, showInvoiced });
	}, [selectedGrouping, showTimeTracking, showInvoiced, setPageState]);

	const { data, options } = useAggregatedDataV2({
		companies: useAllCompanies(),
		clients: useAllClients(),
		projects: useAllProjects(),
		revenueReport: useYearlyRevenueReport(year || ""),
	});

	const headerSlot = useContext(ReportsHeaderSlotContext);
	const {
		companies,
		clients,
		projects,
		revenueReport,
	} = data;

	const { isLoading, loadingPercent } = options;

	// Never let both sources turn off. Turning off the only active source activates the other.
	const toggleTimeTracking = () => {
		if (showTimeTracking && !showInvoiced) {
			setShowInvoiced(true);
		}
		setShowTimeTracking((value) => !value);
	};

	const toggleInvoiced = () => {
		if (showInvoiced && !showTimeTracking) {
			setShowTimeTracking(true);
		}
		setShowInvoiced((value) => !value);
	};

	const [companyChartData, clientChartData, projectChartData] = useMemo(() => {
		if (companies && clients && projects && revenueReport) {
			const timeTracking: RevenueBySource = revenueReport.timeTracking;
			const invoiced: RevenueBySource = revenueReport.invoiced;

			const companyChartData = mergeBreakdown(
				timeTracking.companyBreakdown,
				invoiced.companyBreakdown,
				(breakdown) => breakdown.companyId,
				(id) => companies.find((company) => company.id === id)?.displayName || "",
			);

			const clientChartData = mergeBreakdown(
				timeTracking.clientBreakdown,
				invoiced.clientBreakdown,
				(breakdown) => breakdown.clientId,
				(id) => clients.find((client) => client.id === id)?.displayName || "",
			);

			const projectChartData = mergeBreakdown(
				timeTracking.projectBreakdown,
				invoiced.projectBreakdown,
				(breakdown) => breakdown.projectId,
				(id) => {
					const project = projects.find((project) => project.id === id);
					if(!project) return "";
					return `${project.title} (${project.projectNumber})`;
				}
			);

			return [companyChartData, clientChartData, projectChartData];
		}
		return [];
	}, [companies, clients, projects, revenueReport]);

	const chartData = selectedGrouping === "company"
		? companyChartData
		: selectedGrouping === "project"
			? projectChartData
			: clientChartData;

	return <div className="auto-grid-2-rows h-full gap-y-2 overflow-hidden pb-8 pt-4">
		{isLoading && <LoadingPage pcent={loadingPercent} />}
		{!isLoading && chartData && revenueReport && (<>
			{headerSlot && createPortal(<span className="text-sm font-light">Stand: {formatDateWithGermanMonth(new Date())}</span>, headerSlot)}
			<Card>
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-x-4 text-lg font-bold">
						<span className="text-gray-500">Gesamt {year}: </span>
						{showTimeTracking && <span className="flex items-center gap-x-1.5">
							<span className="size-3 rounded-sm" style={{ backgroundColor: REVENUE_SOURCE_COLORS.timeTracking }} />
							<span className="font-bold">{formatCentsToCurrency(revenueReport.timeTracking.totalRevenueInYear)}</span>
						</span>}
						{showInvoiced && <span className="flex items-center gap-x-1.5">
							<span className="size-3 rounded-sm" style={{ backgroundColor: REVENUE_SOURCE_COLORS.invoiced }} />
							<span className="font-bold">{formatCentsToCurrency(revenueReport.invoiced.totalRevenueInYear)}</span>
						</span>}

					</div>
					<div className="flex items-center gap-x-2">
						<RadioGroupPlain name="revenueGrouping"
										 inputSize="sm"
										 numCols={3}
										 options={groupingOptions}
										 value={selectedGrouping}
										 onChange={setSelectedGrouping}
						/>
						<button type="button"
								onClick={toggleTimeTracking}
								className={clsx(
									"flex items-center gap-x-1.5 rounded px-2 py-1 text-sm transition-colors",
									showTimeTracking ? "text-gray-900" : "text-gray-400",
								)}>
							<span className="size-3 rounded-sm"
								  style={{ backgroundColor: showTimeTracking ? REVENUE_SOURCE_COLORS.timeTracking : "#d1d5db" }} />
							Aufwand
						</button>
						<button type="button"
								onClick={toggleInvoiced}
								className={clsx(
									"flex items-center gap-x-1.5 rounded px-2 py-1 text-sm transition-colors",
									showInvoiced ? "text-gray-900" : "text-gray-400",
								)}>
							<span className="size-3 rounded-sm"
								  style={{ backgroundColor: showInvoiced ? REVENUE_SOURCE_COLORS.invoiced : "#d1d5db" }} />
							Abgerechnet
						</button>
						<DownloadYearlyRevenueReportExcelButton data={chartData}
																type={selectedGrouping}
																year={parseInt(year)} />
					</div>
				</div>
			</Card>
			<Card height="full" className="overflow-y-scroll">
				<YearlyRevenueByProjectChart data={chartData}
											 showTimeTracking={showTimeTracking}
											 showInvoiced={showInvoiced} />
			</Card>
		</>)}

	</div>;
};

export default RevenueReportSection;
